'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2 } from 'lucide-react';

const UserSearchInput = ({ onSelect, onClear, selectedUser, searchFn, disabled, error }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsSearching(true);
    try {
      const data = await searchFn(searchQuery);
      setResults(data || []);
      setIsOpen(data && data.length > 0);
      setHighlightedIndex(-1);
    } finally {
      setIsSearching(false);
    }
  }, [searchFn]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      doSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, doSearch]);

  const handleSelect = (user) => {
    onSelect(user);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  };

  const handleClear = () => {
    onClear();
    setQuery('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => prev < results.length - 1 ? prev + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : results.length - 1);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(results[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Selected user chip
  if (selectedUser) {
    return (
      <div className={`flex items-center gap-2 w-full border rounded-lg px-3 py-2 bg-gray-50 ${error ? 'border-red-500' : 'border-gray-300'}`}>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900">{selectedUser.displayName}</span>
          <span className="text-sm text-gray-500 ml-2">{selectedUser.email}</span>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className={`flex items-center w-full border rounded-lg px-3 py-2 ${error ? 'border-red-500' : 'border-gray-300'} focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white`}>
        {isSearching ? (
          <Loader2 className="w-4 h-4 text-gray-400 animate-spin flex-shrink-0 mr-2" />
        ) : (
          <Search className="w-4 h-4 text-gray-400 flex-shrink-0 mr-2" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search by name or email..."
          className="flex-1 outline-none text-sm bg-transparent"
          disabled={disabled}
        />
      </div>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
            {results.map((user, index) => (
              <button
                key={user.email}
                type="button"
                onClick={() => handleSelect(user)}
                className={`w-full text-left px-3 py-2 hover:bg-gray-100 ${
                  index === highlightedIndex ? 'bg-gray-100' : ''
                }`}
              >
                <div className="text-sm font-medium text-gray-900">{user.displayName}</div>
                <div className="text-xs text-gray-500">
                  {user.email}
                  {user.jobTitle && <span className="ml-2 text-gray-400">- {user.jobTitle}</span>}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {query.length > 0 && query.length < 2 && (
        <p className="text-xs text-gray-400 mt-1">Type at least 2 characters to search</p>
      )}
      {!isSearching && query.length >= 2 && results.length === 0 && !isOpen && (
        <p className="text-xs text-gray-400 mt-1">No users found</p>
      )}
    </div>
  );
};

export default UserSearchInput;
